
import { Component, ElementRef, Injector, OnInit, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import { ContratoService, LicitacaoService, MemorialService, ProponenteService, PropostaService } from 'administrativo-lib';
import {
  BaseResourceFormComponent, Comissao, DateFormatPipe, EddyAutoComplete, FormatoLicitacao, FuncaoService, GlobalService, Licitacao,
  LicitacaoComissaoMembro,
  LicitacaoFiscal,
  LoginContabil, Memorial, MemorialItem, Modalidade, Pagina, Setor, SetorService, SituacaoLicitacao, TipoContratacao, TipoObjetoLicitacao
} from 'eddydata-lib';
import { ConfirmationService, MessageService } from 'primeng/api';
import { takeUntil } from 'rxjs/operators';
import * as toastr from 'toastr';
import { ComissaoService } from '../../comissao/service/comissao.service';
import { ModalidadeService } from '../../modalidade/service/modalidade.service';
import { LicitacaoComissaoMembroService } from '../service/licitacao-comissao-membro.service';

declare var $: any;

@Component({
  selector: 'app-licitacao-form',
  templateUrl: './licitacao-form.component.html'
})
export class LicitacaoFormComponent extends BaseResourceFormComponent<Licitacao, LoginContabil> implements OnInit {

  /**
   * Declaração de variáveis
   */

  // cadastro
  @ViewChild('numero_') inputField: ElementRef;

  public listaSituacoes: { id: SituacaoLicitacao, nome: string }[];
  public listaModalidades: Modalidade[];
  public listaTipoContratacoes: TipoContratacao[];
  public setorAutoComplete: EddyAutoComplete<Setor>;
  public licitacaoAutoComplete: EddyAutoComplete<Licitacao>;
  public listaComissoes: Comissao[];
  public formatosLicitacao: { id: FormatoLicitacao, nome: string }[];
  public listaTipoObjeto: { id: TipoObjetoLicitacao, nome: string }[];
  public listaObjetoLicitacao: { id: number, nome: string }[];
  public listaNatureza: { id: number, nome: string }[];
  public listaTipoLicitacao: { id: number, nome: string }[];
  public listaLei1232006: { id: number, nome: string }[];

  // julgamento
  public julgarPropostasSubmit = false;

  // importar processo
  public licitacaoImportOpt: {
    licitacao: Licitacao,
    processo: boolean,
    datas: boolean,
    justificativa: boolean,
    objeto: boolean,
    fiscais: boolean,
    memorial: boolean,
  }

  // etapas do processo
  public etapaProcesso: boolean;
  public etapaMemorial: boolean;
  public etapaCredenciamento: boolean;
  public etapaProposta: boolean;
  public etapaJulgamento: boolean;
  public etapaContrato: boolean;
  public etapaFase4: boolean;

  /**
   * Construtor com as injeções de dependencias
   */

  constructor(
    protected injector: Injector,
    protected confirmationService: ConfirmationService,
    public funcaoService: FuncaoService,
    private messageService: MessageService,
    protected globalService: GlobalService,
    protected modalidadeService: ModalidadeService,
    protected setorService: SetorService,
    protected comissaoService: ComissaoService,
    protected memorialService: MemorialService,
    protected proponenteService: ProponenteService,
    protected propostaService: PropostaService,
    protected contratoService: ContratoService,
    protected comissaoMembrosService: LicitacaoComissaoMembroService,
    protected licitacaoService: LicitacaoService) {
    super(new Licitacao(), injector, Licitacao.converteJson, licitacaoService);
  }

  // ========================================================================
  //                        MÉTODOS ABSTRAÍDOS
  // ========================================================================

  protected criarCamposForm(): void {
    this.entidadeForm = this.fb.group({
      id: [null],
      formato_contratacao: ['REGISTRO_PRECO', [Validators.required]],
      justificativa: ['', [Validators.required]],
      data_inicio: [null],
      data_termino: [null],
      prazo_pagamento: [null],
      prazo_entrega: [null],
      numero: [null, [Validators.required]],
      sequencia: [1],
      data_abertura: [null, [Validators.required]],
      data_ata: [null],
      data_edital: [null],
      data_julgamento: [null],
      data_parecer: [null],
      data_publicacao: [null],
      data_homologacao: [null],
      data_cancelamento: [null],
      data_assinatura: [null],
      data_ratificacao: [null],
      data_abertura_env: [null],
      data_entrega_env: [null],
      situacao: ['ATIVA', [Validators.required]],
      processo: [null, [Validators.required]],
      objeto: [null, [Validators.required]],
      valor_estimado: [0],
      valor_licitado: [0],
      enviar_transparencia: [false, [Validators.required]],
      criterio_julgamento: ['MENOR_PRECO', [Validators.required]],
      situacao_pregao: [null],
      tipo_pregao: [null],
      criterio_desempate: [null],
      fracassar_auto: [true, [Validators.required]],
      modelo_ata: [null],
      natureza: [null],
      tipo_licitacao: [null],
      objeto_licitacao: [null],
      lei_1232006: [null],
      tipo_objeto: [null],
      modalidade: [null, [Validators.required]],
      orgao: [this.login.orgao, [Validators.required]],
      setor: [null],
      exercicio: [this.login.exercicio, [Validators.required]],
      comissao: [null],
      tipo_contratacao: [null],
      solicitante: [null],
      processo_cc: [null],
      itens: [],
      proponentes: [],
      fiscais: [],
      comissao_membros: [],
    });
  }

  protected campoFoco(): ElementRef {
    if (this.inputField) this.inputField.nativeElement.focus();
    return null;
  }

  protected afterLoad() {
    this.entidade.data_inicio = new DateFormatPipe().transform(this.entidade.data_inicio, []);
    this.entidade.data_termino = new DateFormatPipe().transform(this.entidade.data_termino, []);
    this.entidade.data_abertura = new DateFormatPipe().transform(this.entidade.data_abertura, []);
    this.entidade.data_ata = new DateFormatPipe().transform(this.entidade.data_ata, []);
    this.entidade.data_edital = new DateFormatPipe().transform(this.entidade.data_edital, []);
    this.entidade.data_julgamento = new DateFormatPipe().transform(this.entidade.data_julgamento, []);
    this.entidade.data_parecer = new DateFormatPipe().transform(this.entidade.data_parecer, []);
    this.entidade.data_publicacao = new DateFormatPipe().transform(this.entidade.data_publicacao, []);
    this.entidade.data_homologacao = new DateFormatPipe().transform(this.entidade.data_homologacao, []);
    this.entidade.data_cancelamento = new DateFormatPipe().transform(this.entidade.data_cancelamento, []);
    this.entidade.data_assinatura = new DateFormatPipe().transform(this.entidade.data_assinatura, []);
    this.entidade.data_ratificacao = new DateFormatPipe().transform(this.entidade.data_ratificacao, []);
    this.entidade.data_abertura_env = new DateFormatPipe().transform(this.entidade.data_abertura_env, ['datetime']);
    this.entidade.data_entrega_env = new DateFormatPipe().transform(this.entidade.data_entrega_env, ['datetime']);

    new Promise((resolve, reject) => {
      this.memorialService.obterPorLicitacao(this.entidade.id).pipe(takeUntil(this.unsubscribe))
        .subscribe((res) => {
          this.entidade.itens = res ? res.content : [];

          this.proponenteService.obterPorLicitacao(this.entidade.id, true).pipe(takeUntil(this.unsubscribe))
            .subscribe((res) => {
              this.entidade.proponentes = res ? res.content : [];

              this.contratoService.obterPorLicitacao(this.entidade.id, this.login.orgao.id).pipe(takeUntil(this.unsubscribe))
                .subscribe((res) => {
                  this.entidade.contratos = res ? res.content : [];

                  resolve(true);
                }, error => this.messageService.add({ severity: 'error', summary: 'Atenção', detail: error }));
            }, error => this.messageService.add({ severity: 'error', summary: 'Atenção', detail: error }));
        }, error => this.messageService.add({ severity: 'error', summary: 'Atenção', detail: error }));
    }).then(() => {
      this.comissaoMembrosService.filtrar(0, 0, { licitacao_id: this.entidade.id, relations: 'comissao_membro' }).pipe(takeUntil(this.unsubscribe))
        .subscribe(async (res) => {
          await this.preencherMembros();
          const participantes: LicitacaoComissaoMembro[] = res.content;
          this.entidade.comissao_membros.forEach(m => {
            m.marcado = Boolean(participantes.find(p => p.comissao_membro.id === m.comissao_membro.id)).valueOf();
          })
        })
      this.carregarObjetosLicitacao();
      this.carregarContratacoes();
      this.atribuirEtapasProcesso();
    });
  }

  protected parametrosExtras(): {} {
    return {
      relations: 'modalidade.tipos_contratacao.tipo_contratacao'
        + ',orgao,setor,exercicio,comissao,tipo_contratacao,fiscais.pessoa,licitacao_audesp'
    };
  }

  protected afterInit(): void {
    this.modalidadeService.filtrar(1, -1, {
      'cidade.id': this.login.cidade.id, relations: 'tipos_contratacao.tipo_contratacao', orderBy: 'codigo'
    }).pipe(takeUntil(this.unsubscribe))
      .subscribe((res) => {
        this.listaModalidades = res ? res.content : new Array<Modalidade>();
      }, error => this.messageService.add({ severity: 'error', summary: 'Atenção', detail: error }));

    this.comissaoService.filtrar(1, -1, { 'orgao.id': this.login.orgao.id, relations: 'membros.pessoa', orderBy: 'nome' }).pipe(takeUntil(this.unsubscribe))
      .subscribe((res) => {
        this.listaComissoes = res ? res.content : new Array<Comissao>();
      }, error => this.messageService.add({ severity: 'error', summary: 'Atenção', detail: error }));

    this.setorAutoComplete = new EddyAutoComplete(this.entidadeForm.get('setor'), this.setorService,
      'id', ['codigo', 'nome'],
      { orgao_id: this.login.orgao.id, relations: 'unidade,orgao', orderBy: 'nome' }, { number: ['codigo'], text: ['nome'] });

    this.licitacaoAutoComplete = new EddyAutoComplete(this.entidadeForm.get('processo_cc'), this.licitacaoService,
      'numero', ['numero'], { orgao_id: this.login.orgao.id, orderBy: 'numero', relations: 'modalidade' }, { text: ['numero'] }, () => {
        let licitacaoCC: Licitacao = this.entidadeForm.get('processo_cc').value;
        if (licitacaoCC) {
          this.licitacaoService.obter({ id: licitacaoCC.id, relations: this.parametrosExtras()['relations'] }).pipe(takeUntil(this.unsubscribe))
            .subscribe((res) => {
              licitacaoCC = res;
              this.memorialService.obterPorLicitacao(licitacaoCC.id).pipe(takeUntil(this.unsubscribe))
                .subscribe((res) => {
                  licitacaoCC.itens = res ? res.content : [];
                  this.licitacaoImportOpt = {
                    licitacao: licitacaoCC, datas: true, fiscais: true, justificativa: true, memorial: true, objeto: true, processo: true
                  }
                }, error => this.messageService.add({ severity: 'error', summary: 'Atenção', detail: error }));
            }, error => this.messageService.add({ severity: 'error', summary: 'Atenção', detail: error }));
        }
      });

    this.listaSituacoes = this.globalService.obterSituacoesLicitacao();
    this.formatosLicitacao = this.globalService.obterFormatosLicitacao();
    this.listaTipoObjeto = this.globalService.obterTiposObjetoLicitacao();
    this.listaNatureza = this.globalService.obterNaturezaLicitacao();
    this.listaTipoLicitacao = this.globalService.obterTipoLicitacao()
    this.listaLei1232006 = this.globalService.obterLei1232006Licitacao();
    this.carregarObjetosLicitacao();

    if (this.currentActionRoute === 'novo') {
      this.licitacaoService.proximoNumero(this.login.exercicio.ano)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((res) => {
          this.entidadeForm.get('numero').setValue(res);
        });
    }
  }

  protected beforeSubmit() {
    try {
      if (!this.entidade.id && this.entidadeForm.get('modalidade')?.value?.codigo === 7) {
        this.entidadeForm.get('tipo_pregao').setValue('PROCEDIMENTO_COMUM');
        this.entidadeForm.get('criterio_desempate').setValue('CREDENCIAMENTO');
        this.entidadeForm.get('criterio_julgamento').setValue('MENOR_PRECO');
        this.entidadeForm.get('situacao_pregao').setValue('PENDENTE');
      }

      this.entidadeForm.get('itens').setValue(this.entidade.itens);
      this.entidadeForm.get('fiscais').setValue(this.entidade.fiscais);
      this.entidadeForm.get('proponentes').setValue(this.entidade.proponentes);
      this.entidadeForm.get('comissao_membros').setValue(this.entidade.comissao_membros);
    } catch (e) {
      this.messageService.add({ severity: 'error', summary: 'Validação', detail: e });
      throw e;
    }
  }

  protected afterSubmit(ent: Licitacao) {
    if (this.julgarPropostasSubmit) {
      if (this.entidade.modalidade.codigo === 7) {
        this.router.navigate(['/pregoes', ent.id, 'editar']);
      } else {
        this.router.navigate(['/licitacoes', 'julgamento', ent.id]);
      }
    } else {
      if (this.inclusao) this.router.navigate(['/licitacoes', ent.id, 'editar']);
      else {
        this.entidadeForm.get('valor_licitado').setValue(ent.valor_licitado);
        this.afterLoad();
        this.entidade.data_alteracao = new Date();
      }
    }
  }

  // ========================================================================
  //                            MÉTODOS DA CLASSE
  // ========================================================================

  public carregarContratacoes(atribuirContratacao?: boolean) {
    const modalidade: Modalidade = this.entidadeForm.get('modalidade').value;
    if (modalidade?.tipos_contratacao) {
      this.listaTipoContratacoes = modalidade.tipos_contratacao.map(tc => tc.tipo_contratacao);
      if (this.listaTipoContratacoes.length > 0) {
        this.entidadeForm.get('tipo_contratacao').setValue(
          atribuirContratacao || !this.entidade.tipo_contratacao
            ? this.listaTipoContratacoes[0]
            : this.listaTipoContratacoes.find(t => t.id === this.entidade.tipo_contratacao.id)
        );
      }
    }
  }

  public async preencherMembros() {
    this.entidade.comissao_membros = [];
    this.entidade.comissao = this.entidadeForm.get('comissao').value;
    if (!this.entidade.comissao) return;
    if (!this.entidade.comissao.membros) {
      this.entidade.comissao.membros = this.listaComissoes.find(c => c.id === this.entidadeForm.get('comissao').value.id).membros;
    }
    this.entidade.comissao.membros.forEach(m => {
      this.entidade.comissao_membros.push({ comissao_membro: m, marcado: true })
    })
  }

  public julgarPropostas() {
    this.julgarPropostasSubmit = true;
    this.submitForm();
  }

  public importarProcessoAnterior() {
    if (!this.licitacaoImportOpt?.licitacao) {
      toastr.error('Processo não selecionado para importação!');
      return;
    }

    try {
      if (this.licitacaoImportOpt.processo) {
        this.entidadeForm.get('modalidade').setValue(this.licitacaoImportOpt.licitacao.modalidade);
        this.carregarContratacoes();

        this.entidadeForm.get('tipo_objeto').setValue(this.licitacaoImportOpt.licitacao.tipo_objeto);
        this.carregarObjetosLicitacao();

        this.atribuirValorCC([
          'formato_contratacao', 'processo', 'valor_estimado', 'criterio_julgamento', 'fracassar_auto', 'modalidade', 'setor', 'exercicio', 'comissao', 'tipo_contratacao',
          'natureza', 'tipo_objeto', 'objeto_licitacao', 'tipo_licitacao', 'lei_1232006'
        ]);
      }
      // if (this.licitacaoImportOpt.datas) {
      //   this.atribuirValorCC(['prazo_pagamento', 'prazo_entrega']);
      //   this.atribuirValorCC(['data_inicio', 'data_termino', 'data_abertura', 'data_ata', 'data_edital', 'data_julgamento', 'data_parecer', 'data_publicacao',
      //     'data_homologacao', 'data_cancelamento', 'data_assinatura', 'data_ratificacao', 'data_abertura_env', 'data_entrega_env'], true);
      // }
      if (this.licitacaoImportOpt.objeto) {
        this.atribuirValorCC(['objeto']);
      }
      if (this.licitacaoImportOpt.justificativa) {
        this.atribuirValorCC(['justificativa']);
      }
      if (this.licitacaoImportOpt.fiscais && this.licitacaoImportOpt.licitacao.fiscais?.length > 0) {
        this.entidade.fiscais = []
        for (const fiscal of this.licitacaoImportOpt.licitacao.fiscais) {
          this.entidade.fiscais.push(new LicitacaoFiscal(undefined, fiscal.pessoa, undefined))
        }
      }
      if (this.licitacaoImportOpt.memorial) {
        const itens: Memorial[] = [];
        this.licitacaoImportOpt.licitacao.itens.forEach(item => {
          const m = new Memorial();

          m.descricao = item.descricao;
          m.observacao = item.observacao;
          m.ordem = item.ordem;
          m.ordem_julgamento = item.ordem_julgamento;
          m.produto_unidade = item.produto_unidade;
          m.quantidade = item.quantidade;
          m.unidade = item.unidade;
          m.valor_referencia = item.valor_referencia;
          m.reducao_criterio = item.reducao_criterio;
          m.reducao_lance = item.reducao_lance;

          const itensLote: MemorialItem[] = [];
          item.itens.forEach(itemLote => {
            itensLote.push(new MemorialItem(undefined, itemLote.quantidade, itemLote.descricao, m, itemLote.produto, false));
          })
          m.itens = itensLote;

          itens.push(m);
        })
        if (itens.length > 0) {
          this.entidade.itens = itens;
        }

        toastr.success('Informações importadas com sucesso!');
        $('#dialogImportarProcessoAnterior').modal('hide');
      }
    } catch (err) {
      console.log(err)
      toastr.error('Não foi possível importar informações de processo anterior!');
    }
  }

  private atribuirValorCC(campos: string[], data?: boolean) {
    if (campos && this.licitacaoImportOpt?.licitacao) {
      campos.forEach(campo => {
        let valorCC = this.licitacaoImportOpt.licitacao[campo];
        if (data) valorCC = new DateFormatPipe().transform(this.licitacaoImportOpt.licitacao[campo], [])
        if (valorCC) {
          this.entidadeForm.get(campo)?.setValue(valorCC);
        }
      })
    }
  }

  private atribuirEtapasProcesso() {
    this.etapaProcesso = Boolean(this.entidade.id).valueOf();
    this.etapaMemorial = Boolean(this.entidade.itens?.length > 0).valueOf();
    this.etapaCredenciamento = Boolean(this.entidade.proponentes?.length > 0).valueOf();
    this.etapaProposta = Boolean(this.entidade.proponentes?.find(m => m?.propostas?.length > 0)).valueOf();
    this.etapaJulgamento = Boolean(this.entidade.proponentes?.map(m => m?.propostas).find(p => p?.find(p => p?.situacao === 'VENCEDOR'))?.length > 0).valueOf();
    this.etapaContrato = Boolean(this.entidade.contratos?.length > 0 && this.contratoService.obterContratosPendentesLicitacao(this.entidade)?.length === 0).valueOf();
    this.etapaFase4 = Boolean(this.entidade.licitacao_audesp).valueOf();
  }

  public remover() {
    if (this.entidade.id && !this.etapaMemorial && !this.entidade.enviar_transparencia) {
      this.confirmationService.confirm({
        message: 'Deseja realmente remover o processo?',
        header: 'Exclusão',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Confirmar',
        rejectLabel: 'Cancelar',
        accept: () => {
          this.licitacaoService.remover(this.entidade.id).pipe(takeUntil(this.unsubscribe))
            .subscribe((res) => {
              toastr.info('Registro removido com sucesso!', 'Exclusão');
              this.router.navigate([Pagina["Licitações"]]);
            }, (err) => toastr.error(err.error.payload));
        }
      });
    } else {
      toastr.warning('Processo não pode ser removido!');
    }
  }

  public carregarObjetosLicitacao() {
    this.listaObjetoLicitacao = this.globalService.obterObjetoLicitacao(this.entidadeForm.get('tipo_objeto').value);
  }

}
